import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  Box,
  Typography,
  Avatar,
  ListItemButton,
  Badge,
  Collapse,
  Button
} from '@mui/material';
import {
  Close as CloseIcon,
  Logout as LogoutIcon,
  Brightness4 as Brightness4Icon,
  Brightness7 as Brightness7Icon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Shield as ShieldIcon,
  Description as DescriptionIcon,
  Gavel as GavelIcon,
  Stars as StarsIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useThemeContext } from '../../contexts/ThemeContext';
import { useAuthModal } from '../../hooks/useAuthModal';
import { useSectionSafe, SECTIONS } from '../../contexts/SectionContext';
import { 
  mainNavigationItems, 
  userNavigationItems, 
  isActiveRoute, 
  NavigationItem, 
  filterNavigationItems
} from '../../constants/navigation';

interface MobileDrawerProps {
  open: boolean;
  onClose: () => void;
}

const MobileDrawer: React.FC<MobileDrawerProps> = ({ open, onClose }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { mode, toggleTheme } = useThemeContext();
  const { openLogin, openRegister } = useAuthModal();
  const sectionContext = useSectionSafe();
  const isDarkMode = mode === 'dark';
  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  // Filter navigation items based on auth status
  const visibleMainNavItems = React.useMemo(() => {
    return filterNavigationItems(
      mainNavigationItems, 
      !!user, 
      user?.isAdmin ? ['admin'] : undefined
    );
  }, [user]);

  const visibleUserNavItems = React.useMemo(() => {
    const baseItems = filterNavigationItems(
      userNavigationItems, 
      !!user, 
      user?.isAdmin ? ['admin'] : undefined
    );
    // Analytics is only available to Pro / Business Pro subscribers
    return baseItems.filter(item => {
      if (item.id === 'analytics') {
        return (user as any)?.subscriptionTier === 'pro' || (user as any)?.subscriptionTier === 'business_pro';
      }
      return true;
    });
  }, [user]);

  const handleLogout = async () => {
    try {
      await logout();
      onClose();
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleItemClick = (path: string) => {
    navigate(path);
    onClose();
  };

  const toggleExpandItem = (itemId: string) => {
    setExpandedItems((prev) => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId) 
        : [...prev, itemId]
    );
  };

  const isItemExpanded = (itemId: string) => expandedItems.includes(itemId);

  const renderNavItems = (items: NavigationItem[]) => (
    <>
      {items.map((item) => {
        const isActive = isActiveRoute(location.pathname, item.path, item.exact);
        const hasChildren = item.children && item.children.length > 0;
        const isExpanded = hasChildren && isItemExpanded(item.id || item.path);
        
        return (
          <React.Fragment key={item.path}>
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => {
                  if (hasChildren) {
                    toggleExpandItem(item.id || item.path);
                  } else {
                    handleItemClick(item.path);
                  }
                }}
                selected={isActive}
                sx={{
                  borderRadius: 1,
                  my: 0.25,
                  py: 0.75,
                  minHeight: 40,
                  color: isActive ? 'primary.main' : 'text.primary',
                }}
              >
                <ListItemIcon sx={{ color: isActive ? 'primary.main' : 'inherit', minWidth: 36 }}>
                  {React.createElement(item.icon, { fontSize: 'small' })}
                </ListItemIcon>
                <ListItemText 
                  primary={
                    item.badge?.show ? (
                      <Badge 
                        badgeContent={item.badge.value} 
                        color={item.badge.color || 'error'}
                        max={item.badge.max || 99}
                      >
                        {item.label}
                      </Badge>
                    ) : (
                      item.label
                    )
                  }
                  primaryTypographyProps={{ variant: 'body2', fontSize: '0.875rem' }}
                />
                {hasChildren && (
                  isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />
                )}
              </ListItemButton>
            </ListItem>
            
            {/* Nested menu items */}
            {hasChildren && (
              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <List component="div" disablePadding sx={{ pl: 3 }}>
                  {item.children?.map((child) => {
                    const isChildActive = isActiveRoute(location.pathname, child.path, child.exact);
                    
                    return (
                      <ListItem disablePadding key={child.path}>
                        <ListItemButton
                          onClick={() => handleItemClick(child.path)}
                          selected={isChildActive}
                          sx={{
                            borderRadius: 1,
                            my: 0.25,
                            py: 0.5,
                            minHeight: 36,
                            color: isChildActive ? 'primary.main' : 'text.primary',
                          }}
                        >
                          <ListItemIcon sx={{ color: isChildActive ? 'primary.main' : 'inherit', minWidth: 32 }}>
                            {React.createElement(child.icon, { fontSize: 'small' })}
                          </ListItemIcon>
                          <ListItemText 
                            primary={child.label} 
                            primaryTypographyProps={{ variant: 'body2', fontSize: '0.8rem' }}
                          />
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </List>
              </Collapse>
            )}
          </React.Fragment>
        );
      })}
    </>
  );

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 2, // Ensure drawer is above header
        '& .MuiDrawer-paper': {
          width: { xs: '70vw', sm: 240 }, // 70% of viewport on mobile, 240px on larger screens
          maxWidth: 260,
          boxSizing: 'border-box',
          borderRight: (theme) => `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Drawer header with close button */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            px: 1.5,
            py: 0.5,
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            minHeight: 48,
            flexShrink: 0,
          }}
        >
          <IconButton onClick={onClose} aria-label="Close navigation menu" size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* User info if logged in */}
        {user && (
          <Box
            sx={{
              px: 1.5,
              pt: '70px',
              pb: 1.5,
              display: 'flex',
              alignItems: 'center',
              borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              flexShrink: 0,
            }}
          >
            <Avatar 
              sx={{ 
                bgcolor: 'background.paper',
                color: 'primary.main',
                width: 36,
                height: 36,
                fontSize: '0.95rem',
                fontWeight: 600
              }}
              alt={user.name || 'User'}
            >
              {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
            </Avatar>
            <Box sx={{ ml: 1.5, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }} noWrap>
                {user.name}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.85, lineHeight: 1.2 }} noWrap>
                {user.email}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Section Switcher */}
        {sectionContext && (
          <Box
            sx={{
              px: 1.5,
              pt: user ? 1.5 : '60px',
              pb: 1.5,
              borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
              flexShrink: 0,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: 'text.secondary',
                fontWeight: 600,
                fontSize: '0.65rem',
                display: 'block',
                mb: 1,
              }}
            >
              Sections
            </Typography>
            {Object.values(SECTIONS).map((section) => {
              const isActive = sectionContext.currentSection === section.id;
              return (
                <Box
                  key={section.id}
                  onClick={() => {
                    sectionContext.setSection(section.id);
                    onClose();
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    px: 1.5,
                    py: 1.5,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    mb: 0.5,
                    transition: 'all 0.15s ease',
                    backgroundColor: isActive
                      ? (theme) => `${theme.palette.primary.main}14`
                      : 'transparent',
                    border: isActive
                      ? (theme) => `1px solid ${theme.palette.primary.main}40`
                      : '1px solid transparent',
                    '&:hover': {
                      backgroundColor: (theme) =>
                        isActive
                          ? `${theme.palette.primary.main}14`
                          : theme.palette.action.hover,
                    },
                  }}
                >
                  <Typography component="span" sx={{ fontSize: '1.1rem' }}>
                    {section.icon}
                  </Typography>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{
                        fontSize: '0.82rem',
                        fontWeight: isActive ? 600 : 500,
                        color: isActive ? 'primary.main' : 'text.primary',
                        lineHeight: 1.3,
                      }}
                    >
                      {section.label}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '0.65rem',
                        color: 'text.secondary',
                        lineHeight: 1.2,
                      }}
                    >
                      {section.description}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}

        {/* Scrollable body: nav links + legal */}
        <Box sx={{ flexGrow: 1, overflowY: 'auto', overflowX: 'hidden', minHeight: 0, px: 1, py: 1 }}>
          {/* Navigation links */}
          {/* Only show automotive nav items when not on Everything Else */}
          {(!sectionContext || sectionContext.currentSection !== 'everything-else') && (
            <>
              <List dense disablePadding>
                {renderNavItems(visibleMainNavItems)}
              </List>
              <Divider sx={{ my: 1.5 }} />
            </>
          )}
          
          <List dense disablePadding>
            {renderNavItems(visibleUserNavItems)}
          </List>

          {/* Resources & Legal */}
          <Divider sx={{ my: 1.5 }} />
          <List dense disablePadding>
            {[
              { label: 'Service Standards', path: '/policies/standards', icon: GavelIcon },
              { label: 'Privacy Policy', path: '/policies/privacy', icon: ShieldIcon },
              { label: 'Terms of Service', path: '/policies/terms', icon: DescriptionIcon },
            ].map((item) => (
              <ListItem disablePadding key={item.path}>
                <ListItemButton
                  onClick={() => handleItemClick(item.path)}
                  selected={location.pathname === item.path}
                  sx={{
                    borderRadius: 1,
                    my: 0.25,
                    py: 0.5,
                    minHeight: 36,
                    color: location.pathname === item.path ? 'primary.main' : 'text.secondary',
                  }}
                >
                  <ListItemIcon sx={{ color: 'inherit', minWidth: 32 }}>
                    <item.icon sx={{ fontSize: '1rem' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{ variant: 'body2', fontSize: '0.8rem' }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ fontSize: '0.65rem', px: 1.5, display: 'block', mt: 0.5 }}
          >
            © {new Date().getFullYear()} AutoService. All rights reserved.
          </Typography>
        </Box>

        {/* Upgrade to Pro Banner — TEMPORARILY DISABLED */}

        {/* Footer actions */}
        <Box
          sx={{
            px: 1.5,
            py: 1.5,
            borderTop: (theme) => `1px solid ${theme.palette.divider}`,
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Typography variant="caption" color="text.secondary">
              Dark Mode
            </Typography>
            <IconButton 
              color="inherit" 
              onClick={toggleTheme} 
              aria-label={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
              size="small"
            >
              {isDarkMode ? <Brightness7Icon fontSize="small" /> : <Brightness4Icon fontSize="small" />}
            </IconButton>
          </Box>

          {user ? (
            <Button
              fullWidth
              variant="outlined"
              color="inherit"
              size="small"
              startIcon={<LogoutIcon fontSize="small" />}
              onClick={handleLogout}
              sx={{ textTransform: 'none', py: 0.75 }}
            >
              Sign Out
            </Button>
          ) : (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                color="inherit"
                fullWidth
                size="small"
                onClick={() => {
                  onClose();
                  openLogin();
                }}
                sx={{ textTransform: 'none', py: 0.75 }}
              >
                Log In
              </Button>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                size="small"
                onClick={() => {
                  onClose();
                  openRegister();
                }}
                sx={{ textTransform: 'none', py: 0.75, borderRadius: 1.5 }}
              >
                Sign Up
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default MobileDrawer;
